// @ts-nocheck
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import omitEmpty from 'omit-empty';
import {
  makeStyles,
  Box,
  Grid,
  Icon,
  TextField,
  Button,
  Typography,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Link,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useIfMounted } from '../../../../common/hooks';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      minHeight: '78px',
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    overflow: 'auto',
  },
  sectionTitle: {
    margin: theme.spacing(1, 0),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

function UserForm(props) {
  const [t] = useTranslation();
  const { formState, reset, register, control, handleSubmit } = useForm();
  const classes = useStyles();
  const ifMounted = useIfMounted();

  const {
    onSave,
    onCancel,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
    actionsDisabled,
  } = props;

  const { errors, isSubmitting } = formState;

  const onSubmit = async data => {
    const formData = omitEmpty(data);
    try {
      await onSave(formData);
      ifMounted(() => reset(data));
    } catch (e) {
      // Error already handled
    }
  };

  return (
    <Box id="UserForm-container" className={classes.container}>
      <form id="UserForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                id="UserForm-email"
                name="email"
                label={t('user.userEmail')}
                variant="outlined"
                inputRef={register({
                  required: t('user.userEmailRequired'),
                  pattern: {
                    value:
                      /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/,
                    message: t('user.userEmailInvalid'),
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="UserForm-firstname"
                name="firstname"
                label={t('user.userFirstName')}
                variant="outlined"
                inputRef={register({
                  required: t('user.userFirstNameRequired'),
                })}
                error={!!errors.firstname}
                helperText={errors.firstname?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                id="UserForm-birthYear"
                name="birthYear"
                label={t('user.userBirthYear')}
                variant="outlined"
                inputProps={{ maxLength: 4 }}
                inputRef={register({
                  required: t('user.userBirthYearRequired'),
                  pattern: {
                    value: /^[12][0-9]{3}$/,
                    message: t('user.userBirthYearInvalid'),
                  },
                  min: {
                    value: 1890,
                    message: t('user.userBirthYearMin'),
                  },
                })}
                error={!!errors.birthYear}
                helperText={errors.birthYear?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={7}>
              <Controller
                name="sex"
                control={control}
                defaultValue="U"
                as={
                  <TextField
                    id="UserForm-sex"
                    label={t('user.userSex')}
                    variant="outlined"
                    fullWidth
                    select
                  >
                    <MenuItem value="M">{t('common.male')}</MenuItem>
                    <MenuItem value="F">{t('common.female')}</MenuItem>
                    <MenuItem value="U">{t('common.unknown')}</MenuItem>
                  </TextField>
                }
              />
            </Grid>

            <Grid item xs={7}>
              <TextField
                id="UserForm-cellphone"
                name="cellphone"
                label={t('user.userCellPhone')}
                variant="outlined"
                inputRef={register({
                  pattern: {
                    value: /^[0-9+()#.\s/ext-]+$/,
                    message: t('user.userCellPhoneInvalid'),
                  },
                })}
                error={!!errors.cellphone}
                helperText={errors.cellphone?.message}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.actions}>
          <Button
            id="UserForm-cancel"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(cancelButtonIcon, 'cancel')}</Icon>}
            onClick={onCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(cancelButtonText, t('common.cancel'))}
            </Typography>
          </Button>
          <Button
            id="UserForm-save"
            type="submit"
            color="primary"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'save')}</Icon>}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(saveButtonText, t('common.save'))}
            </Typography>
          </Button>
        </Box>
      </form>
    </Box>
  );
}

UserForm.defaultProps = {
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  saveButtonText: undefined,
  saveButtonIcon: undefined,
  actionsDisabled: undefined,
};

UserForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  saveButtonText: PropTypes.string,
  saveButtonIcon: PropTypes.string,
  actionsDisabled: PropTypes.bool,
};

export default UserForm;
